import { useEffect, useRef, useState } from 'react'
import { RotateCcw, Undo2, X } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'

/** Tag der Löschung — kurz und ohne Uhrzeit, wie in den Projektmasken. */
function alsTag(wert?: string | null): string {
  if (!wert) return '—'
  const datum = new Date(wert)
  if (Number.isNaN(datum.getTime())) return '—'
  return datum.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export interface PapierkorbEintrag {
  id: string
  name: string
  deletedAt: string
  deletedByName: string | null
}

/**
 * Der **Papierkorb** einer Liste an der Position (AP-35 Nachtrag 6 für die
 * Stückliste, Nachtrag 8 für den Verbrauch): gelöschte Zeilen der letzten 30
 * Tage, jede einzeln wiederherstellbar — auch nach dem Neuladen der Seite.
 * Das Fenster hängt unter seinem Knopf (`aria-expanded`); ein Klick daneben
 * oder Esc schließt es.
 *
 * Seit AP-32 Nachtrag 12 (20.9.2026) auch im Belegwesen — darum in `components/`.
 * Dort ist der Papierkorb **nicht** serverseitig: Die Positionen eines Belegs
 * werden als Ganzes gespeichert, gelöschte Zeilen liegen bis zum Verlassen der
 * Maske im Browser. Die Beschriftungen sind deshalb austauschbar.
 */
export function PapierkorbFenster({
  titel,
  laden,
  onWiederherstellen,
  onSchliessen,
  aufbewahrung,
  texte,
}: {
  /** Für Vorleser, z. B. „Papierkorb des Verbrauchs“. */
  titel: string
  laden: () => Promise<PapierkorbEintrag[]>
  onWiederherstellen: (trashId: string) => void
  onSchliessen: () => void
  /** Wie lange der Papierkorb hält — rechts oben im Fenster. */
  aufbewahrung?: string
  /** Beschriftungen für eine Maske, die nicht deutsch fest verdrahtet ist. */
  texte?: { titel?: string; leer?: string; laedt?: string; wiederherstellen?: string; geloescht?: (tag: string, wer: string | null) => string }
}) {
  const [eintraege, setEintraege] = useState<PapierkorbEintrag[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const schliessen = useRef(onSchliessen)
  schliessen.current = onSchliessen
  const ladenRef = useRef(laden)
  ladenRef.current = laden

  useEffect(() => {
    ladenRef
      .current()
      .then(setEintraege)
      .catch((e) => setFehler(apiErrorText(e, 'Der Papierkorb ließ sich nicht laden.')))
  }, [])

  useEffect(() => {
    const weg = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !(e.target as HTMLElement).closest('[aria-expanded]')) schliessen.current()
    }
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && schliessen.current()
    document.addEventListener('mousedown', weg)
    document.addEventListener('keydown', taste)
    return () => {
      document.removeEventListener('mousedown', weg)
      document.removeEventListener('keydown', taste)
    }
  }, [])

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={titel}
      className="absolute right-0 top-full z-50 mt-1 w-[24rem] max-w-[calc(100vw-2rem)] rounded-lg border border-border p-3 text-sm shadow-xl"
      style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium">{texte?.titel ?? 'Papierkorb'}</span>
        <span className="text-xs text-muted-foreground">{aufbewahrung ?? '30 Tage aufbewahrt'}</span>
      </div>
      {fehler && <p className="text-destructive">{fehler}</p>}
      {!eintraege && !fehler && <p className="text-muted-foreground">{texte?.laedt ?? 'Lädt …'}</p>}
      {eintraege?.length === 0 && <p className="text-muted-foreground">{texte?.leer ?? 'Keine gelöschten Zeilen.'}</p>}
      <div className="max-h-72 overflow-auto">
        {eintraege?.map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-2 border-t border-border py-1.5 first:border-t-0">
            <span className="min-w-0">
              <span className="block truncate">{e.name}</span>
              <span className="block text-[11px] text-muted-foreground">
                {texte?.geloescht
                  ? texte.geloescht(alsTag(e.deletedAt), e.deletedByName)
                  : `gelöscht ${alsTag(e.deletedAt)}${e.deletedByName ? ` von ${e.deletedByName}` : ''}`}
              </span>
            </span>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded border border-border px-2 py-0.5 text-xs hover:bg-muted"
              onClick={() => {
                onWiederherstellen(e.id)
                setEintraege((l) => l?.filter((x) => x.id !== e.id) ?? null)
              }}
            >
              <RotateCcw size={12} /> {texte?.wiederherstellen ?? 'wiederherstellen'}
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Die Zeile „… gelöscht. [Rückgängig]“ über der Liste: holt die zuletzt
 * gelöschte Zeile zurück, bei mehreren nacheinander die jeweils jüngste.
 */
export function RueckgaengigHinweis({
  geloescht,
  onRueckgaengig,
  onSchliessen,
  texte,
}: {
  geloescht: { trashId: string; name: string }[]
  onRueckgaengig: (trashId: string) => void
  onSchliessen: () => void
  texte?: { satz?: (name: string, anzahl: number) => string; rueckgaengig?: string; schliessen?: string }
}) {
  const letzte = geloescht[geloescht.length - 1]
  if (!letzte) return null
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm" data-auswahl-behalten>
      <span>
        {texte?.satz
          ? texte.satz(letzte.name, geloescht.length)
          : `„${letzte.name}“ gelöscht${geloescht.length > 1 ? ` (${geloescht.length} Zeilen)` : ''}.`}
      </span>
      <button type="button" className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-muted" onClick={() => onRueckgaengig(letzte.trashId)}>
        <Undo2 size={13} /> {texte?.rueckgaengig ?? 'Rückgängig'}
      </button>
      <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={texte?.schliessen ?? 'Hinweis schließen'} onClick={onSchliessen}>
        <X size={14} />
      </button>
    </p>
  )
}
